import { useFragment } from '@apollo/client';
import type { TeamStatsProps } from '@ps/isp-web-component-library';
import { TeamStats } from '@ps/isp-web-component-library';
import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import type { FootballStats } from '@ps/web-shared-generated-graphql/sports';
import { isEmpty, isNumber } from 'lodash';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import type { LiveData } from '@swf/sports/utils/getLiveData';
import { getIsFootballLiveData } from '@swf/sports/utils/getLiveData';

import { useSportsEventInfoWidget } from '../../model';

import {
  TXTWCL_Corners_2,
  TXTWCL_Fouls,
  TXTWCL_Offsides,
  TXTWCL_Possession_XNX,
  TXTWCL_Red_Cards,
  TXTWCL_Saves,
  TXTWCL_Shots_Off_Target,
  TXTWCL_Shots_On_Target,
  TXTWCL_Total_Shots,
  TXTWCL_Yellow_Cards
} from './MatchStats.messages.json';
import type { MatchStatsProps, StatType } from './types';

const orderedStats: StatType[] = [
  'possession',
  'totalShots',
  'shotsOnTarget',
  'shotsOffTarget',
  'corners',
  'fouls',
  'yellowCards',
  'redCards',
  'offsides',
  'goalkeeperSaves'
];

const ROW_HEIGHT = 35;
const CONTAINER_OFFSET_WEB = 60;
const CONTAINER_OFFSET_MOBILE = 10;

const getTeamStatsData = (
  fullTimeStats: FootballStats,
  translationMap: Record<StatType, string>
) => {
  if (!fullTimeStats.home || !fullTimeStats.away) {
    return { teamStats: {} };
  }

  const teamStats: TeamStatsProps['teamStats'] = {};
  orderedStats.forEach((statType) => {
    const homeValue = fullTimeStats.home?.[statType];
    const awayValue = fullTimeStats.away?.[statType];
    if (!isNumber(homeValue) || !isNumber(awayValue)) {
      return;
    }
    teamStats[statType] = {
      stats: [homeValue, awayValue],
      label: translationMap[statType]
    };
  });

  return {
    teamStats
  };
};

const MatchStats: React.FC<MatchStatsProps> = ({
  eventId,
  eventTypeId,
  isEventInPlay,
  showTeamStats
}) => {
  const { widgetDispatch } = useSportsEventInfoWidget();
  const { variables } = useLiveData();
  const options = getEventScoreboardFragment(eventTypeId, eventId, variables);
  const { data } = useFragment<LiveData>(options);
  const footballData = getIsFootballLiveData(eventTypeId, data) ? data : null;
  const { stats: teamStats } = footballData || {};
  const fullTimeStats = teamStats?.find(
    (stat) => stat?.periodStatus === 'FULL' && stat.period === null
  );
  const hasTeamStats = !isEmpty(fullTimeStats) && isEventInPlay;
  const { formatMessage } = useIntl();
  const { greaterThan } = useScreenSize();

  const translationMap: Record<StatType, string> = {
    possession: formatMessage(TXTWCL_Possession_XNX),
    totalShots: formatMessage(TXTWCL_Total_Shots),
    shotsOnTarget: formatMessage(TXTWCL_Shots_On_Target),
    shotsOffTarget: formatMessage(TXTWCL_Shots_Off_Target),
    corners: formatMessage(TXTWCL_Corners_2),
    fouls: formatMessage(TXTWCL_Fouls),
    yellowCards: formatMessage(TXTWCL_Yellow_Cards),
    redCards: formatMessage(TXTWCL_Red_Cards),
    offsides: formatMessage(TXTWCL_Offsides),
    goalkeeperSaves: formatMessage(TXTWCL_Saves)
  };

  useEffect(() => {
    widgetDispatch.setHasTeamStats(hasTeamStats);
  }, [widgetDispatch, hasTeamStats]);

  if (isEmpty(fullTimeStats)) {
    return null;
  }

  const teamStatsData: TeamStatsProps = getTeamStatsData(fullTimeStats, translationMap);
  const containerHeight =
    Object.keys(teamStatsData.teamStats).length * ROW_HEIGHT +
    (greaterThan(Size.XSmall) ? CONTAINER_OFFSET_WEB : CONTAINER_OFFSET_MOBILE);

  return showTeamStats ? (
    <div style={{ height: containerHeight }}>
      <TeamStats {...teamStatsData} />
    </div>
  ) : null;
};

export default MatchStats;
